/**
 * Five Hundred — server-authoritative rules engine (partnership trick-taking, 4 players, 43-card deck).
 *
 * Rules implemented (toggles live in {@link FiveHundredConfig}):
 *  - 4 players in two partnerships: seats 0 & 2 against seats 1 & 3. The pack is the 52-card deck without the
 *    2s, 3s and black 4s, plus one joker (43 cards). Each player gets 10 cards and 3 go face down to the kitty.
 *  - Bidding starts left of the dealer and goes round the table, as many times as needed. A bid is a number of tricks
 *    (6..10) and a suit or no-trump, and must be worth more than the previous bid (value = 40 + 20·suit + 100·(tricks−6),
 *    suits ranked ♠ ♣ ♦ ♥ then no-trump). A player who passes is out of the auction. If everyone passes the hand is thrown
 *    in and redealt (after `maxRedeals` the dealer is forced to bid the lowest contract).
 *  - The winner takes the kitty, discards three cards and leads. Misère is not played.
 *  - Trumps rank joker, right bower (jack of trumps), left bower (the other jack of the same colour, which counts as
 *    a trump), then A K Q 10 9 … In a no-trump contract the joker is the top card and always wins; the player who
 *    leads it names the suit the others must follow.
 *  - Players must follow the led suit (a left bower follows trumps, not its printed suit). A void player may play anything.
 *  - Scoring: a declarer team that takes at least its bid scores the bid's value (10 tricks is worth at least 250);
 *    otherwise it loses that value. The other team scores 10 for each trick it takes.
 *  - The match ends when the declaring team reaches `targetScore`, or the other team does, or a team falls to
 *    `-targetScore`, or after `maxRounds`. Partners share a place, so payouts are split between the two partners.
 */
import { isPrintedJoker, makeCardId, parseCard, removeCards, sortHand, RANKS, type CardId, type Rank, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface FiveHundredConfig {
  targetScore: number;
  maxRounds: number;
  /** After this many consecutive thrown-in hands the dealer must bid. */
  maxRedeals: number;
  roundEndDelayMs: number;
  trickEndDelayMs: number;
}

export type FHSuit = Suit | 'N';
export interface FHContract {
  tricks: number;
  suit: FHSuit;
}

export type FHPhase = 'BIDDING' | 'KITTY' | 'PLAYING' | 'TRICK_END' | 'ROUND_END' | 'MATCH_END';

export interface FHTrickPlay {
  seat: number;
  card: CardId;
}

export interface FHCompletedTrick {
  leader: number;
  plays: FHTrickPlay[];
  winner: number;
  /** The suit that had to be followed. */
  ledSuit: Suit | null;
}

export interface FHTeamRound {
  declarer: boolean;
  tricks: number;
  delta: number;
}

export interface FHRoundResult {
  contract: FHContract;
  declarer: number;
  made: boolean;
  teams: FHTeamRound[];
}

export interface FiveHundredState {
  config: FiveHundredConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: FHPhase;
  hands: CardId[][];
  kitty: CardId[];
  /** Per seat: the seat's latest bid (null = not yet bid). */
  bids: (FHContract | null)[];
  passed: boolean[];
  highBid: { seat: number; contract: FHContract } | null;
  redeals: number;
  trump: FHSuit | null;
  tricksWon: number[];
  turn: number;
  trick: FHTrickPlay[];
  /** Suit to follow in the current trick (named by the leader when a joker is led in no-trump). */
  ledSuit: Suit | null;
  tricks: FHCompletedTrick[];
  teamScores: number[];
  roundResults: FHRoundResult[];
}

export type FiveHundredAction =
  | { type: 'bid'; tricks: number; suit: FHSuit }
  | { type: 'pass' }
  | { type: 'discard'; cards: CardId[] }
  | { type: 'play'; card: CardId; suit?: Suit };

export type FiveHundredLegal =
  | { kind: 'bid'; minValue: number; canPass: boolean }
  | { kind: 'discard'; count: number }
  | { kind: 'play'; cards: CardId[]; jokerNeedsSuit: boolean }
  | { kind: 'none' };

export interface FiveHundredView {
  gameType: 'FIVE_HUNDRED';
  config: FiveHundredConfig;
  seat: number | null;
  phase: FHPhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  bids: (FHContract | null)[];
  passed: boolean[];
  highBid: { seat: number; contract: FHContract } | null;
  declarer: number | null;
  trump: FHSuit | null;
  tricksWon: number[];
  trick: FHTrickPlay[];
  ledSuit: Suit | null;
  lastTrick: FHCompletedTrick | null;
  tricks: FHCompletedTrick[];
  teamScores: number[];
  roundResults: FHRoundResult[];
  totals: number[];
}

const DEFAULTS: FiveHundredConfig = {
  targetScore: 500,
  maxRounds: 14,
  maxRedeals: 4,
  roundEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;
const HAND = 10;
const KITTY = 3;
const JOKER = 'JK:0';
const SUIT_RANK: Record<FHSuit, number> = { S: 0, C: 1, D: 2, H: 3, N: 4 };
export const FH_SUITS: readonly FHSuit[] = ['S', 'C', 'D', 'H', 'N'];

export const fhTeamOf = (seat: number): number => seat % 2;
export const fhPartnerOf = (seat: number): number => (seat + 2) % SEATS;

export function fhBidValue(c: FHContract): number {
  return 40 + 20 * SUIT_RANK[c.suit] + 100 * (c.tricks - 6);
}

/** Every legal contract, cheapest first (6♠ = 40 … 10 no-trump = 520). */
export function fhAllContracts(): FHContract[] {
  const out: FHContract[] = [];
  for (let tricks = 6; tricks <= 10; tricks++) for (const suit of FH_SUITS) out.push({ tricks, suit });
  return out;
}

export function fhContractsAbove(value: number): FHContract[] {
  return fhAllContracts().filter((c) => fhBidValue(c) > value);
}

/** The 43-card pack: no 2s or 3s, no black 4s, plus a joker. */
export function fhDeck(): CardId[] {
  const out: CardId[] = [];
  for (const suit of ['S', 'H', 'D', 'C'] as const) {
    for (const rank of RANKS) {
      if (rank <= 3) continue;
      if (rank === 4 && (suit === 'S' || suit === 'C')) continue;
      out.push(makeCardId(rank as Rank, suit));
    }
  }
  out.push(JOKER);
  return out;
}

const MATE: Record<Suit, Suit> = { S: 'C', C: 'S', H: 'D', D: 'H' };

/**
 * The suit a card counts as under the contract's trump: the joker is a trump, as is the left bower; in no-trump the
 * joker belongs to no suit ('J').
 */
export function fhEffectiveSuit(card: CardId, trump: FHSuit): Suit | 'J' {
  if (isPrintedJoker(card)) return trump === 'N' ? 'J' : trump;
  const c = parseCard(card);
  if (trump !== 'N' && c.rank === 11 && c.suit === MATE[trump]) return trump;
  return c.suit;
}

/** Strength within its suit (trumps include the joker and the bowers). */
export function fhPower(card: CardId, trump: FHSuit): number {
  if (isPrintedJoker(card)) return 300;
  const c = parseCard(card);
  if (trump !== 'N' && c.rank === 11) {
    if (c.suit === trump) return 200;
    if (c.suit === MATE[trump]) return 190;
  }
  return c.rank;
}

export function fhIsTrump(card: CardId, trump: FHSuit): boolean {
  return trump !== 'N' && fhEffectiveSuit(card, trump) === trump;
}

/** Winner of a complete (or partial) trick. `ledSuit` is the suit that had to be followed. */
export function fhTrickWinner(plays: readonly FHTrickPlay[], trump: FHSuit, ledSuit: Suit | null): number {
  if (trump === 'N') {
    const joker = plays.find((p) => isPrintedJoker(p.card));
    if (joker) return joker.seat;
  } else {
    const trumps = plays.filter((p) => fhEffectiveSuit(p.card, trump) === trump);
    if (trumps.length > 0) return trumps.reduce((a, b) => (fhPower(b.card, trump) > fhPower(a.card, trump) ? b : a)).seat;
  }
  const followers = plays.filter((p) => fhEffectiveSuit(p.card, trump) === ledSuit);
  const pool = followers.length > 0 ? followers : [plays[0]!];
  return pool.reduce((a, b) => (fhPower(b.card, trump) > fhPower(a.card, trump) ? b : a)).seat;
}

export function fhLegalCards(hand: readonly CardId[], trick: readonly FHTrickPlay[], trump: FHSuit, ledSuit: Suit | null): CardId[] {
  if (trick.length === 0 || ledSuit === null) return [...hand];
  const followers = hand.filter((c) => fhEffectiveSuit(c, trump) === ledSuit);
  // In no-trump the joker may be played at any time.
  if (trump === 'N') {
    const joker = hand.filter((c) => isPrintedJoker(c));
    return followers.length > 0 ? [...followers, ...joker] : [...hand];
  }
  return followers.length > 0 ? followers : [...hand];
}

/** Round score for the declaring team and the defenders. */
export function fhScoreRound(contract: FHContract, declarerTricks: number): { declarer: number; defender: number; made: boolean } {
  const value = fhBidValue(contract);
  const made = declarerTricks >= contract.tricks;
  const slam = declarerTricks === 10;
  return {
    made,
    declarer: made ? (slam ? Math.max(value, 250) : value) : -value,
    defender: (10 - declarerTricks) * 10,
  };
}

/** Three cards worth discarding from the 13: spare low cards, ideally emptying a short side suit. */
export function fhChooseDiscard(hand: readonly CardId[], trump: FHSuit): CardId[] {
  const keep = (card: CardId) => {
    const eff = fhEffectiveSuit(card, trump);
    const sideCount = hand.filter((h) => fhEffectiveSuit(h, trump) === eff).length;
    const power = fhPower(card, trump);
    if (fhIsTrump(card, trump) || isPrintedJoker(card)) return 1000 + power;
    // Low cards in short suits are the easiest to get rid of; aces and kings are worth keeping.
    return power + (power >= 13 ? 30 : 0) + (sideCount <= 2 ? -3 : 0) + sideCount * 0.5;
  };
  return [...hand].sort((a, b) => keep(a) - keep(b)).slice(0, KITTY);
}

function deal(state: FiveHundredState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(fhDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  for (let s = 0; s < SEATS; s++) hands[s] = deck.slice(s * HAND, (s + 1) * HAND);
  state.hands = hands.map((h) => sortHand(h));
  state.kitty = deck.slice(SEATS * HAND);
  state.bids = [null, null, null, null];
  state.passed = [false, false, false, false];
  state.highBid = null;
  state.trump = null;
  state.tricksWon = [0, 0, 0, 0];
  state.trick = [];
  state.ledSuit = null;
  state.tricks = [];
  state.phase = 'BIDDING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, counts: state.hands.map((h) => h.length) });
}

/** Next seat still in the auction. The current high bidder is never asked again: the auction ends once the rest pass. */
function nextBidder(state: FiveHundredState, from: number): number {
  for (let i = 1; i <= SEATS; i++) {
    const s = (from + i) % SEATS;
    if (!state.passed[s] && s !== state.highBid?.seat) return s;
  }
  return from;
}

function isForcedBid(state: FiveHundredState, seat: number): boolean {
  return state.highBid === null && state.redeals >= state.config.maxRedeals && state.passed.filter(Boolean).length === SEATS - 1 && !state.passed[seat];
}

function teamStandings(state: FiveHundredState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = fhTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}

export const fiveHundredEngine: GameEngine<FiveHundredConfig, FiveHundredState, FiveHundredAction, FiveHundredView, FiveHundredLegal> = {
  gameType: 'FIVE_HUNDRED',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 100, 2000);
    checkInt('maxRounds', c.maxRounds, 1, 40);
    checkInt('maxRedeals', c.maxRedeals, 0, 20);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Five Hundred needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: FiveHundredState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'BIDDING',
      hands: [],
      kitty: [],
      bids: [],
      passed: [],
      highBid: null,
      redeals: 0,
      trump: null,
      tricksWon: [],
      turn: 0,
      trick: [],
      ledSuit: null,
      tricks: [],
      teamScores: [0, 0],
      roundResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'pass') return { type: 'pass' };
    if (raw.type === 'bid' && typeof raw.tricks === 'number' && Number.isInteger(raw.tricks) && typeof raw.suit === 'string' && (FH_SUITS as readonly string[]).includes(raw.suit)) {
      return { type: 'bid', tricks: raw.tricks, suit: raw.suit as FHSuit };
    }
    if (raw.type === 'discard' && Array.isArray(raw.cards) && raw.cards.length === KITTY && raw.cards.every((c) => typeof c === 'string' && c.length <= 5)) {
      return { type: 'discard', cards: raw.cards as CardId[] };
    }
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 5) {
      if (raw.suit === undefined) return { type: 'play', card: raw.card };
      if (typeof raw.suit === 'string' && ['S', 'H', 'D', 'C'].includes(raw.suit)) return { type: 'play', card: raw.card, suit: raw.suit as Suit };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Five Hundred action');
  },

  applyAction(state, seat, action, rng) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && (state.phase === 'BIDDING' || state.phase === 'KITTY' || state.phase === 'PLAYING'), 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (action.type === 'bid' || action.type === 'pass') {
      assertEngine(state.phase === 'BIDDING', 'INVALID_PHASE', 'Bidding is closed');
      if (action.type === 'pass') {
        assertEngine(!isForcedBid(state, seat), 'ILLEGAL_MOVE', 'You must bid: every other player has passed too many times');
        state.passed[seat] = true;
        events.push({ type: 'pass', seat });
      } else {
        assertEngine(action.tricks >= 6 && action.tricks <= 10, 'INVALID_AMOUNT', 'Bid out of range');
        const contract = { tricks: action.tricks, suit: action.suit };
        assertEngine(fhBidValue(contract) > (state.highBid ? fhBidValue(state.highBid.contract) : 0), 'INVALID_AMOUNT', 'A bid must beat the previous one');
        state.bids[seat] = contract;
        state.highBid = { seat, contract };
        events.push({ type: 'bid', seat, tricks: contract.tricks, suit: contract.suit });
      }
      const live = state.passed.filter((p) => !p).length;
      const rivals = state.passed.filter((p, i) => !p && i !== state.highBid?.seat).length;
      if (state.highBid && rivals === 0) {
        const winner = state.highBid;
        state.trump = winner.contract.suit;
        state.hands[winner.seat]!.push(...state.kitty);
        state.hands[winner.seat] = sortHand(state.hands[winner.seat]!);
        state.kitty = [];
        state.phase = 'KITTY';
        state.turn = winner.seat;
        state.redeals = 0;
        events.push({ type: 'biddingComplete', declarer: winner.seat, tricks: winner.contract.tricks, suit: winner.contract.suit });
      } else if (!state.highBid && live === 0) {
        // Thrown in: redeal with the next dealer.
        state.redeals++;
        events.push({ type: 'thrownIn' });
        state.dealer = (state.dealer + 1) % SEATS;
        deal(state, rng, events);
      } else {
        state.turn = nextBidder(state, seat);
      }
      return events;
    }

    if (action.type === 'discard') {
      assertEngine(state.phase === 'KITTY', 'INVALID_PHASE', 'Not discarding now');
      const hand = state.hands[seat]!;
      assertEngine(new Set(action.cards).size === KITTY && action.cards.every((c) => hand.includes(c)), 'INVALID_CARD', 'Choose three cards from your hand');
      removeCards(hand, action.cards);
      state.phase = 'PLAYING';
      state.turn = seat;
      events.push({ type: 'discarded', seat });
      return events;
    }

    assertEngine(state.phase === 'PLAYING', 'INVALID_PHASE', 'Not in play phase');
    const hand = state.hands[seat]!;
    const trump = state.trump!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(fhLegalCards(hand, state.trick, trump, state.ledSuit).includes(action.card), 'ILLEGAL_MOVE', 'That card is not allowed now');
    if (state.trick.length === 0) {
      if (trump === 'N' && isPrintedJoker(action.card)) {
        assertEngine(action.suit !== undefined, 'INVALID_ACTION', 'Name the suit when leading the joker');
        state.ledSuit = action.suit!;
      } else {
        state.ledSuit = fhEffectiveSuit(action.card, trump) as Suit;
      }
    }
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card, ...(state.trick.length === 1 && state.ledSuit ? { ledSuit: state.ledSuit } : {}) });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = fhTrickWinner(state.trick, trump, state.ledSuit);
    state.tricksWon[winner]!++;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner, ledSuit: state.ledSuit });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'BIDDING' || state.phase === 'KITTY' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'BIDDING') return { kind: 'bid', minValue: state.highBid ? fhBidValue(state.highBid.contract) : 0, canPass: !isForcedBid(state, seat) };
    if (state.phase === 'KITTY') return { kind: 'discard', count: KITTY };
    if (state.phase === 'PLAYING') {
      const trump = state.trump!;
      const cards = fhLegalCards(state.hands[seat]!, state.trick, trump, state.ledSuit);
      return { kind: 'play', cards, jokerNeedsSuit: trump === 'N' && state.trick.length === 0 && cards.some((c) => isPrintedJoker(c)) };
    }
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'BIDDING') {
      if (!isForcedBid(state, seat)) return { type: 'pass' };
      const min = fhAllContracts().find((c) => fhBidValue(c) > (state.highBid ? fhBidValue(state.highBid.contract) : 0))!;
      return { type: 'bid', tricks: min.tricks, suit: min.suit };
    }
    if (state.phase === 'KITTY') return { type: 'discard', cards: fhChooseDiscard(state.hands[seat]!, state.trump!) };
    const trump = state.trump!;
    const legal = fhLegalCards(state.hands[seat]!, state.trick, trump, state.ledSuit);
    const lowest = [...legal].sort((a, b) => (fhIsTrump(a, trump) ? 1000 : 0) + fhPower(a, trump) - ((fhIsTrump(b, trump) ? 1000 : 0) + fhPower(b, trump)))[0]!;
    if (trump === 'N' && state.trick.length === 0 && isPrintedJoker(lowest)) return { type: 'play', card: lowest, suit: 'S' };
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'FIVE_HUNDRED',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      bids: state.bids.map((b) => (b ? { ...b } : null)),
      passed: [...state.passed],
      highBid: state.highBid ? { seat: state.highBid.seat, contract: { ...state.highBid.contract } } : null,
      declarer: state.phase === 'BIDDING' ? null : (state.highBid?.seat ?? null),
      trump: state.trump,
      tricksWon: [...state.tricksWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      ledSuit: state.phase === 'TRICK_END' ? (state.tricks.at(-1)?.ledSuit ?? null) : state.ledSuit,
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      teamScores: [...state.teamScores],
      roundResults: state.roundResults.map((r) => ({ ...r, contract: { ...r.contract }, teams: r.teams.map((t) => ({ ...t })) })),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[fhTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      state.ledSuit = null;
      if (state.tricks.length < HAND) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const cfg = state.config;
      const { seat: declarerSeat, contract } = state.highBid!;
      const declarerTeam = fhTeamOf(declarerSeat);
      const declarerTricks = state.tricksWon[declarerTeam]! + state.tricksWon[declarerTeam + 2]!;
      const pts = fhScoreRound(contract, declarerTricks);
      const teams: FHTeamRound[] = [0, 1].map((team) => ({
        declarer: team === declarerTeam,
        tricks: state.tricksWon[team]! + state.tricksWon[team + 2]!,
        delta: team === declarerTeam ? pts.declarer : pts.defender,
      }));
      teams.forEach((t, team) => {
        state.teamScores[team]! += t.delta;
      });
      state.roundResults.push({ contract, declarer: declarerSeat, made: pts.made, teams });
      events.push({ type: 'roundEnd', round: state.round, contract, declarer: declarerSeat, made: pts.made, teams, teamScores: [...state.teamScores] });
      const [a, b] = state.teamScores as [number, number];
      const target = cfg.targetScore;
      const declarerWins = pts.made && state.teamScores[declarerTeam]! >= target;
      const otherReached = state.teamScores[1 - declarerTeam]! >= target;
      const busted = a <= -target || b <= -target;
      const capped = state.round >= cfg.maxRounds;
      if (((declarerWins || otherReached || busted) && a !== b) || capped) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'ROUND_END';
      }
      return events;
    }
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};
